import { useEffect, useRef, useState } from 'react';
import { Trash2, Upload, Image as ImageIcon, Film, Music, Plus, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('casa_admin_token')}`,
});

/* ============================================================
   Vixen profiles — image + name + bio, used for the coverflow
   ============================================================ */
export function VixenManager() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState('');
  const [bio, setBio] = useState('');
  const [file, setFile] = useState(null);
  const [order, setOrder] = useState(0);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const fileRef = useRef();

  const load = async () => {
    const res = await fetch(`${API_URL}/api/admin/vixens`, { headers: authHeaders() });
    if (res.ok) {
      const j = await res.json();
      setItems(j.items || []);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!file || !name.trim()) {
      setMsg('Photo and name are required');
      return;
    }
    setBusy(true);
    setMsg('');
    const fd = new FormData();
    fd.append('file', file);
    fd.append('name', name);
    fd.append('bio', bio);
    fd.append('order', order);
    fd.append('active', 'true');
    const res = await fetch(`${API_URL}/api/admin/vixens`, {
      method: 'POST',
      headers: authHeaders(),
      body: fd,
    });
    if (res.ok) {
      setName(''); setBio(''); setFile(null); setOrder(0);
      if (fileRef.current) fileRef.current.value = '';
      await load();
      setMsg('Vixen added');
    } else {
      const j = await res.json().catch(() => ({}));
      setMsg(j.detail || 'Upload failed');
    }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this vixen profile?')) return;
    await fetch(`${API_URL}/api/admin/vixens/${id}`, { method: 'DELETE', headers: authHeaders() });
    await load();
  };

  const toggleActive = async (item) => {
    await fetch(`${API_URL}/api/admin/vixens/${item.id}`, {
      method: 'PATCH',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ active: !item.active }),
    });
    await load();
  };

  return (
    <div className="space-y-6" data-testid="admin-vixens-panel">
      <div className="bg-[#111022] border border-white/5 rounded-xl p-5">
        <h2 className="text-base font-semibold text-[#F4F2FF] mb-1">Add Vixen</h2>
        <p className="text-xs text-[#A7A3BA] mb-4">Photo + name + short bio. Appears in the client coverflow.</p>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input
            type="text"
            placeholder="Name (e.g. Candy)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="px-3 py-2 bg-[#07060B] border border-white/10 rounded-lg text-sm text-[#F4F2FF] focus:outline-none focus:border-[#FF4DA6]/60"
            data-testid="vixen-name"
          />
          <input
            type="number"
            placeholder="Order (0 = first)"
            value={order}
            onChange={(e) => setOrder(parseInt(e.target.value || '0', 10))}
            className="px-3 py-2 bg-[#07060B] border border-white/10 rounded-lg text-sm text-[#F4F2FF] focus:outline-none focus:border-[#FF4DA6]/60"
            data-testid="vixen-order"
          />
          <textarea
            placeholder="Bio (1–2 short sentences)"
            rows={2}
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            className="md:col-span-2 px-3 py-2 bg-[#07060B] border border-white/10 rounded-lg text-sm text-[#F4F2FF] focus:outline-none focus:border-[#FF4DA6]/60 resize-none"
            data-testid="vixen-bio"
          />
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={(e) => setFile(e.target.files[0])}
            className="md:col-span-2 text-xs text-[#A7A3BA] file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-[#FF4DA6]/15 file:text-[#FFB6D8] hover:file:bg-[#FF4DA6]/25"
            data-testid="vixen-file"
          />
          <button
            type="submit"
            disabled={busy}
            className="md:col-span-2 inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#FF4DA6] hover:bg-[#FF4DA6]/90 text-white rounded-lg text-sm font-medium disabled:opacity-50"
            data-testid="vixen-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add Vixen
          </button>
          {msg && <p className="md:col-span-2 text-xs text-[#FFB6D8]">{msg}</p>}
        </form>
      </div>

      <div className="bg-[#111022] border border-white/5 rounded-xl p-5">
        <h2 className="text-base font-semibold text-[#F4F2FF] mb-3">Existing Vixens ({items.length})</h2>
        {items.length === 0 ? (
          <p className="text-sm text-[#A7A3BA]">No vixens yet.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {items.map((v) => (
              <div key={v.id} className="bg-[#07060B] border border-white/5 rounded-lg overflow-hidden" data-testid={`vixen-card-${v.id}`}>
                <img src={`${API_URL}${v.photo_url}`} alt={v.name} className="w-full aspect-square object-cover" />
                <div className="p-3 space-y-2">
                  <p className="text-sm font-medium text-[#F4F2FF] truncate">{v.name}</p>
                  {v.bio && <p className="text-xs text-[#A7A3BA] line-clamp-2">{v.bio}</p>}
                  <div className="flex items-center justify-between gap-2 pt-1">
                    <button
                      onClick={() => toggleActive(v)}
                      className={`text-xs px-2 py-1 rounded ${v.active ? 'bg-green-500/15 text-green-300' : 'bg-white/5 text-[#A7A3BA]'}`}
                      data-testid={`vixen-toggle-${v.id}`}
                    >
                      {v.active ? 'Active' : 'Hidden'}
                    </button>
                    <button
                      onClick={() => remove(v.id)}
                      className="text-xs px-2 py-1 rounded bg-red-500/15 text-red-300 hover:bg-red-500/25 inline-flex items-center gap-1"
                      data-testid={`vixen-delete-${v.id}`}
                    >
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   Media items — images + videos shown in the coverflow
   ============================================================ */
export function MediaManager() {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState('');
  const [order, setOrder] = useState(0);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const fileRef = useRef();

  const load = async () => {
    const res = await fetch(`${API_URL}/api/admin/media`, { headers: authHeaders() });
    if (res.ok) setItems((await res.json()).items || []);
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { setMsg('Pick a file'); return; }
    setBusy(true); setMsg('');
    const fd = new FormData();
    fd.append('file', file);
    fd.append('title', title);
    fd.append('order', order);
    fd.append('active', 'true');
    const res = await fetch(`${API_URL}/api/admin/media`, {
      method: 'POST', headers: authHeaders(), body: fd,
    });
    if (res.ok) {
      setTitle(''); setOrder(0); setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      await load();
      setMsg('Media added');
    } else {
      const j = await res.json().catch(() => ({}));
      setMsg(j.detail || 'Upload failed');
    }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this media item?')) return;
    await fetch(`${API_URL}/api/admin/media/${id}`, { method: 'DELETE', headers: authHeaders() });
    await load();
  };

  const toggleActive = async (item) => {
    await fetch(`${API_URL}/api/admin/media/${item.id}`, {
      method: 'PATCH',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ active: !item.active }),
    });
    await load();
  };

  return (
    <div className="space-y-6" data-testid="admin-media-panel">
      <div className="bg-[#111022] border border-white/5 rounded-xl p-5">
        <h2 className="text-base font-semibold text-[#F4F2FF] mb-1">Add Media</h2>
        <p className="text-xs text-[#A7A3BA] mb-4">Image (JPG/PNG/WEBP/GIF, ≤25MB) or Video (MP4/MOV/WEBM, ≤200MB).</p>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input
            type="text" placeholder="Optional title"
            value={title} onChange={(e) => setTitle(e.target.value)}
            className="px-3 py-2 bg-[#07060B] border border-white/10 rounded-lg text-sm text-[#F4F2FF] focus:outline-none focus:border-[#8B5CFF]/60"
            data-testid="media-title"
          />
          <input
            type="number" placeholder="Order" value={order}
            onChange={(e) => setOrder(parseInt(e.target.value || '0', 10))}
            className="px-3 py-2 bg-[#07060B] border border-white/10 rounded-lg text-sm text-[#F4F2FF] focus:outline-none focus:border-[#8B5CFF]/60"
            data-testid="media-order"
          />
          <input
            ref={fileRef} type="file"
            accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm"
            onChange={(e) => setFile(e.target.files[0])}
            className="md:col-span-2 text-xs text-[#A7A3BA] file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-[#8B5CFF]/15 file:text-[#C8B6FF] hover:file:bg-[#8B5CFF]/25"
            data-testid="media-file"
          />
          <button
            type="submit" disabled={busy}
            className="md:col-span-2 inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#8B5CFF] hover:bg-[#8B5CFF]/90 text-white rounded-lg text-sm font-medium disabled:opacity-50"
            data-testid="media-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Upload
          </button>
          {msg && <p className="md:col-span-2 text-xs text-[#C8B6FF]">{msg}</p>}
        </form>
      </div>

      <div className="bg-[#111022] border border-white/5 rounded-xl p-5">
        <h2 className="text-base font-semibold text-[#F4F2FF] mb-3">Library ({items.length})</h2>
        {items.length === 0 ? (
          <p className="text-sm text-[#A7A3BA]">No media uploaded yet.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {items.map((m) => (
              <div key={m.id} className="bg-[#07060B] border border-white/5 rounded-lg overflow-hidden" data-testid={`media-card-${m.id}`}>
                <div className="aspect-square bg-black flex items-center justify-center">
                  {m.kind === 'image' ? (
                    <img src={`${API_URL}${m.url}`} alt={m.title || 'media'} className="w-full h-full object-cover" />
                  ) : (
                    <video src={`${API_URL}${m.url}`} className="w-full h-full object-cover" muted playsInline />
                  )}
                </div>
                <div className="p-3 space-y-2">
                  <div className="flex items-center gap-2 text-xs text-[#A7A3BA]">
                    {m.kind === 'image' ? <ImageIcon className="w-3 h-3" /> : <Film className="w-3 h-3" />}
                    <span className="truncate">{m.title || m.kind}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <button
                      onClick={() => toggleActive(m)}
                      className={`text-xs px-2 py-1 rounded ${m.active ? 'bg-green-500/15 text-green-300' : 'bg-white/5 text-[#A7A3BA]'}`}
                      data-testid={`media-toggle-${m.id}`}
                    >
                      {m.active ? 'Active' : 'Hidden'}
                    </button>
                    <button
                      onClick={() => remove(m.id)}
                      className="text-xs px-2 py-1 rounded bg-red-500/15 text-red-300 hover:bg-red-500/25 inline-flex items-center gap-1"
                      data-testid={`media-delete-${m.id}`}
                    >
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   Background music — single track override
   ============================================================ */
export function MusicManager() {
  const [current, setCurrent] = useState(null);
  const [title, setTitle] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const fileRef = useRef();

  const load = async () => {
    const res = await fetch(`${API_URL}/api/music`);
    if (res.ok) setCurrent(await res.json());
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { setMsg('Pick an audio file'); return; }
    setBusy(true); setMsg('');
    const fd = new FormData();
    fd.append('file', file);
    fd.append('title', title);
    const res = await fetch(`${API_URL}/api/admin/music`, {
      method: 'POST', headers: authHeaders(), body: fd,
    });
    if (res.ok) {
      setTitle(''); setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      await load();
      setMsg('Music updated');
    } else {
      const j = await res.json().catch(() => ({}));
      setMsg(j.detail || 'Upload failed');
    }
    setBusy(false);
  };

  const remove = async () => {
    if (!window.confirm('Remove the current background track?')) return;
    await fetch(`${API_URL}/api/admin/music`, { method: 'DELETE', headers: authHeaders() });
    await load();
  };

  return (
    <div className="space-y-6" data-testid="admin-music-panel">
      <div className="bg-[#111022] border border-white/5 rounded-xl p-5">
        <h2 className="text-base font-semibold text-[#F4F2FF] mb-1">Background Music</h2>
        <p className="text-xs text-[#A7A3BA] mb-4">MP3 / WAV / OGG / AAC, ≤25MB. Plays softly under the coverflow when visitors un-mute.</p>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input
            type="text" placeholder="Track title (optional)"
            value={title} onChange={(e) => setTitle(e.target.value)}
            className="md:col-span-2 px-3 py-2 bg-[#07060B] border border-white/10 rounded-lg text-sm text-[#F4F2FF] focus:outline-none focus:border-[#27D7FF]/60"
            data-testid="music-title"
          />
          <input
            ref={fileRef} type="file"
            accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/aac"
            onChange={(e) => setFile(e.target.files[0])}
            className="md:col-span-2 text-xs text-[#A7A3BA] file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-[#27D7FF]/15 file:text-[#7EE6FF] hover:file:bg-[#27D7FF]/25"
            data-testid="music-file"
          />
          <button
            type="submit" disabled={busy}
            className="md:col-span-2 inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#27D7FF] hover:bg-[#27D7FF]/90 text-[#07060B] rounded-lg text-sm font-medium disabled:opacity-50"
            data-testid="music-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Music className="w-4 h-4" />}
            {current?.url ? 'Replace track' : 'Upload track'}
          </button>
          {msg && <p className="md:col-span-2 text-xs text-[#7EE6FF]">{msg}</p>}
        </form>
      </div>

      <div className="bg-[#111022] border border-white/5 rounded-xl p-5">
        <h2 className="text-base font-semibold text-[#F4F2FF] mb-3">Currently Playing</h2>
        {current?.url ? (
          <div className="space-y-3">
            <p className="text-sm text-[#F4F2FF]">{current.title || 'Untitled track'}</p>
            <audio src={`${API_URL}${current.url}`} controls className="w-full" data-testid="music-current-audio" />
            <button
              onClick={remove}
              className="text-xs px-3 py-1.5 rounded bg-red-500/15 text-red-300 hover:bg-red-500/25 inline-flex items-center gap-1"
              data-testid="music-remove"
            >
              <Trash2 className="w-3 h-3" /> Remove
            </button>
          </div>
        ) : (
          <p className="text-sm text-[#A7A3BA]">No background track set.</p>
        )}
      </div>
    </div>
  );
}
